import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { downloadEndorsementPdf, generateEndorsementPdf } from "@/lib/endorsement";
import type { AuthUser, Candidate, RatingLevel, CriterionRow, EndorsementInput } from "@/types";
import { scoreToRating } from "@/types";

const RATING_LABELS: Record<RatingLevel, string> = {
  exceeds: "Exceeds (3)",
  meets: "Meets (2)",
  below: "Below (1)",
};

function todayLong(): string {
  return new Date().toLocaleDateString(undefined, {
    month: "long",
    day: "2-digit",
    year: "numeric",
  });
}

function deriveBreakdownRow(
  candidate: Candidate,
  matcher: RegExp,
  fallback: { label: string; remarks?: string },
  options: { bulletPoints?: boolean } = {}
): CriterionRow {
  const breakdown = candidate.matrix_breakdown ?? [];
  const matched = breakdown.filter((b) => matcher.test(b.criterion));
  if (matched.length === 0) {
    return {
      label: fallback.label,
      rating: scoreToRating(candidate.match_score),
      remarks: fallback.remarks ?? "",
    };
  }
  const avg = matched.reduce((s, b) => s + b.score, 0) / matched.length;
  const prefix = options.bulletPoints ? "• " : "";
  const remarks = matched
    .map((b) => `${prefix}${b.criterion}${b.notes ? `: ${b.notes}` : ""}`)
    .join("\n");
  return {
    label: fallback.label,
    rating: scoreToRating(avg),
    remarks,
  };
}

export function EndorsementDialog({
  candidate,
  positionTitle: positionTitleProp,
  currentUser,
  onClose,
}: {
  candidate: Candidate;
  positionTitle: string;
  currentUser?: AuthUser;
  onClose: () => void;
}) {
  const positionTitle = candidate.position_title || positionTitleProp;
  const { data: profileData, isLoading } = useQuery({
    queryKey: queryKeys.candidateProfile(candidate.id),
    queryFn: () => api.getCandidateProfile(candidate.id),
  });

  const profile = (profileData?.profile || {}) as Record<string, unknown>;
  const salary = (profileData?.salary || {}) as Record<string, unknown>;
  const fullCandidate = (profileData?.candidate || {}) as Record<string, unknown>;

  // Derived defaults
  const defaultEducation = useMemo((): CriterionRow => {
    const breakdown = candidate.matrix_breakdown ?? [];
    const matched = breakdown.filter((b) => /educat|degree|licens|certif/i.test(b.criterion));
    if (matched.length === 0) {
      return { label: "Education / Licenses", rating: "meets", remarks: candidate.education || "" };
    }
    const avg = matched.reduce((s, b) => s + b.score, 0) / matched.length;
    // "Exceeds (3)" only when a license/certification criterion is present and high-scoring
    const hasLicense = matched.some((b) => /licens|certif/i.test(b.criterion));
    const rating: RatingLevel = avg >= 80 && hasLicense ? "exceeds" : avg >= 60 ? "meets" : "below";
    const remarks = matched.map((b) => `• ${b.criterion}${b.notes ? `: ${b.notes}` : ""}`).join("\n");
    return { label: "Education / Licenses", rating, remarks };
  }, [candidate]);
  const defaultExperience = useMemo(
    () =>
      deriveBreakdownRow(
        candidate,
        /experien|work|year/i,
        {
          label: "Related Experience",
          remarks: candidate.years_experience != null ? `• ${candidate.years_experience} years of experience` : "",
        },
        { bulletPoints: true }
      ),
    [candidate]
  );
  const defaultSkills = useMemo(
    () =>
      deriveBreakdownRow(candidate, /skill|competen|tool|tech/i, {
        label: "Skills / Competency",
        remarks: candidate.skills?.length ? candidate.skills.join(", ") : "",
      }),
    [candidate]
  );
  const defaultOverall: CriterionRow = useMemo(
    () => ({
      label: "Overall Impression",
      rating: scoreToRating(candidate.match_score),
      remarks: candidate.summary || "",
    }),
    [candidate]
  );

  const [date, setDate] = useState(todayLong());
  const [intro, setIntro] = useState(
    "Attached herewith is the result of our initial assessment and reference for your further evaluation. Should you find him/her close to your requirements, please feel free to tell us your convenient time to interview the candidate so we can arrange accordingly."
  );

  const expectedSalaryDefault =
    (salary?.expected_salary as number | undefined) != null
      ? `Php ${Number(salary.expected_salary).toLocaleString()}`
      : "";
  const [currentSalary, setCurrentSalary] = useState("");
  const [expectedSalary, setExpectedSalary] = useState(expectedSalaryDefault);
  const [availability, setAvailability] = useState("");

  const [education, setEducation] = useState<CriterionRow>(defaultEducation);
  const [experience, setExperience] = useState<CriterionRow>(defaultExperience);
  const [skills, setSkills] = useState<CriterionRow>(defaultSkills);
  const [overall, setOverall] = useState<CriterionRow>(defaultOverall);
  const [recruiterRemarks, setRecruiterRemarks] = useState("");
  const [resumeText, setResumeText] = useState("");
  const resumeUserEdited = useRef(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const shareCopyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (expectedSalaryDefault && !expectedSalary) {
      setExpectedSalary(expectedSalaryDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedSalaryDefault]);

  useEffect(() => {
    const stored =
      (fullCandidate.cleaned_resume_text as string | undefined) ||
      (fullCandidate.raw_resume as string | undefined) ||
      (fullCandidate.resume_text as string | undefined) ||
      "";
    if (stored && !resumeUserEdited.current) setResumeText(stored);
  }, [fullCandidate.cleaned_resume_text, fullCandidate.raw_resume, fullCandidate.resume_text]);

  const handleResumeTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    resumeUserEdited.current = true;
    setResumeText(e.target.value);
  }, []);

  const buildInput = (): EndorsementInput => {
    const personal = {
      birth_date: profile.date_of_birth as string | undefined,
      nationality: profile.nationality as string | undefined,
      civil_status: profile.marital_status as string | undefined,
      birth_place: [profile.city, profile.state, profile.country].filter(Boolean).join(", ") || undefined,
    };
    const educationItems = Array.isArray(profile.education_history)
      ? (profile.education_history as Array<{ school: string; degree: string; year: string }>)
      : [];
    const workItems = Array.isArray(profile.work_experience)
      ? (profile.work_experience as Array<{ company: string; title: string; from: string; to: string; description: string }>)
      : [];
    return {
      organizationName: currentUser?.org_name || "PipeTalent AI",
      orgBanner: currentUser?.org_banner ?? null,
      orgLogo: currentUser?.org_logo ?? null,
      date,
      candidateName: candidate.name || "Unknown candidate",
      positionTitle,
      introParagraph: intro,
      currentSalary,
      expectedSalary,
      availability,
      educationRow: education,
      experienceRow: experience,
      skillsRow: skills,
      overallRow: overall,
      recruiterRemarks,
      photoDataUrl: candidate.photo,
      personal,
      education: educationItems,
      work: workItems,
      resumeText,
      resumeFilename: candidate.filename || undefined,
    };
  };

  const openPreview = () => {
    const doc = generateEndorsementPdf(buildInput());
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const download = () => {
    downloadEndorsementPdf(buildInput());
    closePreview();
    onClose();
  };

  const sharePdf = async () => {
    setSharing(true);
    try {
      const { id } = await api.createShare(JSON.stringify(buildInput()));
      const url = `${window.location.origin}/share/pdf?id=${id}`;
      setShareLink(url);
      await navigator.clipboard.writeText(url);
      if (shareCopyTimerRef.current) clearTimeout(shareCopyTimerRef.current);
      shareCopyTimerRef.current = setTimeout(() => setShareLink(null), 4000);
    } catch {
      // ignore — button stays in normal state
    } finally {
      setSharing(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-card bg-white shadow-card">
        <div className="flex items-center justify-between border-b border-surface-secondary px-5 py-3">
          <div>
            <h3 className="text-lg font-semibold text-near-black">Generate Endorsement Form</h3>
            <p className="text-xs text-text-secondary">Pre-filled from screening — edit anything before generating.</p>
          </div>
          <button onClick={onClose} className="rounded-btn px-2 py-1 text-text-secondary hover:bg-surface-secondary hover:text-near-black">
            ✕
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <p className="text-sm text-text-secondary">Loading profile…</p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Date">
                  <input value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
                </Field>
                <Field label="Position">
                  <input value={positionTitle} disabled className={`${inputCls} bg-surface-secondary text-text-secondary`} />
                </Field>
                <Field label="Current / Last Salary">
                  <input value={currentSalary} onChange={(e) => setCurrentSalary(e.target.value)} placeholder="Php 33,000" className={inputCls} />
                </Field>
                <Field label="Expected Salary">
                  <input value={expectedSalary} onChange={(e) => setExpectedSalary(e.target.value)} placeholder="Php 35,000" className={inputCls} />
                </Field>
                <Field label="Availability to Start">
                  <input value={availability} onChange={(e) => setAvailability(e.target.value)} placeholder="Required for 30-day notice period" className={inputCls} />
                </Field>
              </div>

              <Field label="Intro paragraph">
                <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={3} className={`${inputCls} resize-y`} />
              </Field>

              <div className="space-y-3 rounded-card border border-surface-secondary p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">Assessment</p>
                <CriterionEditor label="EDUCATION / LICENSES" row={education} onChange={setEducation} />
                <CriterionEditor label="RELATED EXPERIENCE" row={experience} onChange={setExperience} />
                <CriterionEditor label="SKILLS / COMPETENCY" row={skills} onChange={setSkills} />
                <CriterionEditor label="OVERALL IMPRESSION" row={overall} onChange={setOverall} />
              </div>

              <Field label="Recruiter remarks (optional)">
                <textarea value={recruiterRemarks} onChange={(e) => setRecruiterRemarks(e.target.value)} rows={3} placeholder="Anything else worth flagging to the hiring manager…" className={`${inputCls} resize-y`} />
              </Field>

              <Field label={`Candidate resume / CV — appended after page 1 (${resumeText.length.toLocaleString()} characters)`}>
                {!resumeText && (
                  <div className="mb-1.5 rounded-btn border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                    No resume text on file for this candidate. Paste it below, or close this dialog and use “Re-upload resume” to attach a fresh one.
                  </div>
                )}
                <textarea
                  value={resumeText}
                  onChange={handleResumeTextChange}
                  rows={8}
                  placeholder="Paste the candidate's resume text here…"
                  className={`${inputCls} resize-y font-mono text-xs leading-relaxed`}
                />
              </Field>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-surface-secondary px-5 py-3">
          <button onClick={onClose} className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary hover:text-near-black">
            Cancel
          </button>
          <button onClick={openPreview} disabled={isLoading} className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50">
            Preview PDF
          </button>
        </div>
      </div>
    </div>

    {previewUrl && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4">
        <div className="flex h-[92vh] w-full max-w-4xl flex-col rounded-card bg-white shadow-card">
          <div className="flex items-center justify-between border-b border-surface-secondary px-5 py-3">
            <h3 className="text-base font-semibold text-near-black">
              {candidate.name ? `${candidate.name} Endorsement Form` : "Endorsement Form"}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={sharePdf}
                disabled={sharing}
                className="rounded-btn border border-border-card px-4 py-2 text-sm font-medium text-near-black transition-colors hover:bg-surface-secondary disabled:opacity-50"
              >
                {shareLink ? "Link copied!" : sharing ? "Creating link…" : "Share"}
              </button>
              <button
                onClick={download}
                className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch"
              >
                Download PDF
              </button>
              <button
                onClick={closePreview}
                className="rounded-btn px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-secondary hover:text-near-black"
              >
                ✕
              </button>
            </div>
          </div>
          <iframe
            src={previewUrl}
            className="flex-1 w-full rounded-b-card"
            title="Endorsement PDF Preview"
          />
        </div>
      </div>
    )}
    </>
  );
}

const inputCls =
  "w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="font-semibold text-text-secondary">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function CriterionEditor({
  label,
  row,
  onChange,
}: {
  label: string;
  row: CriterionRow;
  onChange: (r: CriterionRow) => void;
}) {
  return (
    <div className="space-y-1.5 border-t border-surface-secondary pt-3 first:border-0 first:pt-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-near-black">{label}</p>
        <div className="flex gap-1">
          {(Object.keys(RATING_LABELS) as RatingLevel[]).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => onChange({ ...row, rating: r })}
              className={`rounded-badge px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                row.rating === r
                  ? r === "exceeds"
                    ? "bg-emerald-600 text-white"
                    : r === "meets"
                    ? "bg-amber-500 text-white"
                    : "bg-rose-600 text-white"
                  : "bg-surface-secondary text-text-secondary hover:bg-near-black hover:text-white"
              }`}
            >
              {RATING_LABELS[r]}
            </button>
          ))}
        </div>
      </div>
      <textarea
        value={row.remarks}
        onChange={(e) => onChange({ ...row, remarks: e.target.value })}
        rows={3}
        className={`${inputCls} resize-y`}
        placeholder="Remarks…"
      />
    </div>
  );
}
